// What the form still needs, worked out ONCE per change of answers and shared: the
// fields that get the "still needed" mark (Question), the progress strip's count and
// its jump to the next one (ProgressBar), and whether a section's Calculate button is
// the next step yet (CalculateButton).
//
// Kevin, 2026-10-13, on the NDB page: "do you think its easy to focus on what i need to
// here?" It was not -- the strip said "3 to go" but the three looked like every other
// field. This is the same list Save & Submit checks (collectRequiredVisible), so a
// field is never marked needed here and then waved through by the submit, or the
// other way round.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { CpqData } from "../data/loader";
import { useAnswers } from "./AnswersContext";
import { collectRequiredVisible, type RequiredField } from "../util/validation";

type RequiredFieldsCtx = {
  /** Every field required right now, answered or not, in the order the form shows them. */
  fields: RequiredField[];
  /** Codes of the required fields still unanswered. */
  pending: ReadonlySet<string>;
  /** Sections (group ids) with at least one of those. */
  pendingGroups: ReadonlySet<string>;
  /** The first unanswered field in form order, or null when nothing is left. */
  next: RequiredField | null;
};

const EMPTY: RequiredFieldsCtx = {
  fields: [],
  pending: new Set(),
  pendingGroups: new Set(),
  next: null,
};

const Ctx = createContext<RequiredFieldsCtx>(EMPTY);

export function RequiredFieldsProvider({ data, children }: { data: CpqData; children: ReactNode }) {
  const { answers, mirrorRequiredCells } = useAnswers();
  const value = useMemo<RequiredFieldsCtx>(() => {
    const fields = collectRequiredVisible(data, answers, { requiredCells: mirrorRequiredCells });
    const open = fields.filter((f) => !f.answered);
    return {
      fields,
      pending: new Set(open.map((f) => f.code)),
      pendingGroups: new Set(open.map((f) => f.groupId)),
      next: open[0] ?? null,
    };
  }, [data, answers, mirrorRequiredCells]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Outside the provider (nothing renders a Question there today) everything reads as done. */
export function useRequiredFields(): RequiredFieldsCtx {
  return useContext(Ctx);
}
